import React, {Component} from 'react';
import {withTranslation} from 'react-i18next';
import {FaIcon} from '../../FaIcon';
import {GA4FiltersPreview} from './GA4FiltersPreview';
import {fetchGa4DimensionValues} from './ga4FiltersPreviewApi';
import {metaFirst} from './ga4MetaValues';
import {FACEBOOK_CHANNEL} from '../../channels/channels';
import '../../../sass/elements/_ga4-filters-editor.scss';

export const FIELD_SOURCE = 'SOURCE';
export const FIELD_MEDIUM = 'MEDIUM';
export const FIELD_CAMPAIGN = 'CAMPAIGN';
export const FIELD_LANDING_PAGE = 'LANDING_PAGE';
export const FIELD_PRODUCT_NAME = 'PRODUCT_NAME';
export const FIELD_PRODUCT_ID = 'PRODUCT_ID';
export const FIELD_TERM = 'TERM';
export const FIELD_CONTENT = 'CONTENT';

export const OPERATOR_EQUALS = 'EQUALS';
export const OPERATOR_CONTAINS = 'CONTAINS';
export const OPERATOR_IN_LIST = 'IN_LIST';
export const OPERATOR_REGEXP_PARTIAL_MATCH = 'REGEXP_PARTIAL_MATCH';

// Fuente única de las dimensiones de filtro GA4 disponibles. La consumen los dos
// caminos de asociación (directo a la Línea de Control y dentro de una cuenta
// publicitaria) para garantizar que ofrezcan exactamente las mismas opciones.
// Término y Contenido van al final para no mover el orden de siempre.
export const GA4_AVAILABLE_FIELDS = [
    FIELD_SOURCE, FIELD_MEDIUM, FIELD_CAMPAIGN, FIELD_LANDING_PAGE,
    FIELD_PRODUCT_NAME, FIELD_PRODUCT_ID,
    FIELD_TERM, FIELD_CONTENT,
];

const DEFAULT_AVAILABLE_FIELDS = GA4_AVAILABLE_FIELDS;

// En el combo van agrupados bajo «Nuevos»: un <select> nativo no puede dibujar una pastilla
// al lado de la opción, y así el combo cerrado dice «Término (utm_term)» limpio.
const NEW_FIELDS = [FIELD_TERM, FIELD_CONTENT];

const ALL_OPERATORS = [
    OPERATOR_CONTAINS, OPERATOR_EQUALS, OPERATOR_IN_LIST, OPERATOR_REGEXP_PARTIAL_MATCH,
];

const MULTI_VALUE_OPERATORS = [OPERATOR_CONTAINS, OPERATOR_IN_LIST];

const MAX_SUGGESTIONS = 8;

const FIELD_TO_ACCENT_CLASS = {
    [FIELD_SOURCE]: 'field-source',
    [FIELD_MEDIUM]: 'field-medium',
    [FIELD_CAMPAIGN]: 'field-campaign',
    [FIELD_LANDING_PAGE]: 'field-landing_page',
    [FIELD_PRODUCT_NAME]: 'field-product_name',
    [FIELD_PRODUCT_ID]: 'field-product_id',
    [FIELD_TERM]: 'field-term',
    [FIELD_CONTENT]: 'field-content',
};

class GA4FiltersEditorClass extends Component {
    constructor(props) {
        super(props);
        this.state = {
            // texto "en progreso" dentro del chip-input, por cada fila
            editingTextByIndex: {},
            // Autocompletar con los valores reales de la property (`propertyId`): por campo, la
            // lista que devolvió GA4; la fila con el desplegable abierto y la opción resaltada.
            suggestionsByField: {},
            suggestionsOpenIndex: null,
            highlightedSuggestion: -1,
        };
        this.handleFieldChange = this.handleFieldChange.bind(this);
        this.handleOperatorChange = this.handleOperatorChange.bind(this);
        this.handleSingleValueChange = this.handleSingleValueChange.bind(this);
        this.handleAddFilter = this.handleAddFilter.bind(this);
        this.handleRemoveFilter = this.handleRemoveFilter.bind(this);
    }

    componentDidMount() {
        this._mounted = true;
    }

    componentWillUnmount() {
        this._mounted = false;
    }

    availableFields() {
        return this.props.availableFields && this.props.availableFields.length > 0
            ? this.props.availableFields
            : DEFAULT_AVAILABLE_FIELDS;
    }

    currentFilters() {
        return Array.isArray(this.props.filters) ? this.props.filters : [];
    }

    emitChange(newFilters) {
        if (this.props.onChange) {
            this.props.onChange(newFilters);
        }
    }

    handleFieldChange(index, event) {
        const newFilters = [...this.currentFilters()];
        newFilters[index] = {...newFilters[index], field: event.target.value};
        this.emitChange(newFilters);
    }

    handleOperatorChange(index, event) {
        const newOperator = event.target.value;
        const newFilters = [...this.currentFilters()];
        const existing = newFilters[index] || {};
        let newValues = Array.isArray(existing.values) ? [...existing.values] : [];

        // Si cambia a operador de valor único (EQUALS/REGEXP), conservar solo el primero
        if (!MULTI_VALUE_OPERATORS.includes(newOperator) && newValues.length > 1) {
            newValues = newValues.slice(0, 1);
        }
        newFilters[index] = {...existing, operator: newOperator, values: newValues};

        // Limpiar buffer de edición al cambiar operador
        this._clearEditingText(index);
        this.emitChange(newFilters);
    }

    handleSingleValueChange(index, event) {
        const text = event.target.value;
        const newFilters = [...this.currentFilters()];
        const existing = newFilters[index] || {};
        newFilters[index] = {...existing, values: text === '' ? [] : [text]};
        this.emitChange(newFilters);
    }

    // El filtro nuevo nace en el primer campo que la línea todavía no usa. Nacía siempre en
    // Fuente, y quien agregaba "cpc" sin cambiar el campo armaba "Fuente contiene google" Y
    // "Fuente contiene cpc", que no deja pasar ninguna sesión (15 líneas así en prod, 23/09).
    nextUnusedField() {
        const fields = this.availableFields();
        const used = new Set(this.currentFilters().map(f => f.field || fields[0]));
        return fields.find(f => !used.has(f)) || fields[0];
    }

    // Un filtro sin valores no cuenta: el backend lo descarta, así que no combina con nada.
    duplicatedFields() {
        const fields = this.availableFields();
        const seen = new Set();
        const duplicated = new Set();
        this.currentFilters().filter(f => Array.isArray(f.values) && f.values.length > 0).forEach(f => {
            const field = f.field || fields[0];
            if (seen.has(field)) duplicated.add(field);
            seen.add(field);
        });
        return [...duplicated];
    }

    handleAddFilter() {
        const newFilters = [...this.currentFilters(), {
            field: this.nextUnusedField(),
            operator: OPERATOR_CONTAINS,
            values: [],
        }];
        this.emitChange(newFilters);
    }

    handleRemoveFilter(index) {
        const newFilters = this.currentFilters().filter((_, i) => i !== index);
        this._clearEditingText(index);
        this.emitChange(newFilters);
    }

    _clearEditingText(index) {
        this.setState(prev => {
            const next = {...prev.editingTextByIndex};
            delete next[index];
            return {editingTextByIndex: next};
        });
    }

    _setEditingText(index, text) {
        this.setState(prev => ({
            editingTextByIndex: {...prev.editingTextByIndex, [index]: text},
        }));
    }

    _commitEditingChip(index) {
        this._addValue(index, this.state.editingTextByIndex[index] || '');
    }

    _addValue(index, rawText) {
        const text = String(rawText).trim();
        if (!text) return;

        const newFilters = [...this.currentFilters()];
        const existing = newFilters[index] || {};
        const currentValues = Array.isArray(existing.values) ? existing.values : [];

        // Evitar duplicados
        if (currentValues.includes(text)) {
            this._clearEditingText(index);
            return;
        }

        newFilters[index] = {...existing, values: [...currentValues, text]};
        this._clearEditingText(index);
        this.emitChange(newFilters);
    }

    _removeChipAt(index, chipIndex) {
        const newFilters = [...this.currentFilters()];
        const existing = newFilters[index] || {};
        const currentValues = Array.isArray(existing.values) ? existing.values : [];
        newFilters[index] = {...existing, values: currentValues.filter((_, i) => i !== chipIndex)};
        this.emitChange(newFilters);
    }

    _handleChipInputKeyDown(index, event) {
        const text = this.state.editingTextByIndex[index] || '';

        const suggestions = this._matchingSuggestions(index);
        if (suggestions.length > 0 && (event.key === 'ArrowDown' || event.key === 'ArrowUp')) {
            event.preventDefault();
            const step = event.key === 'ArrowDown' ? 1 : -1;
            const next = (this.state.highlightedSuggestion + step + suggestions.length) % suggestions.length;
            this.setState({suggestionsOpenIndex: index, highlightedSuggestion: next});
            return;
        }
        if (event.key === 'Escape') {
            this._closeSuggestions();
            return;
        }
        if (event.key === 'Enter' && this.state.highlightedSuggestion >= 0
            && suggestions[this.state.highlightedSuggestion]) {
            event.preventDefault();
            this._pickSuggestion(index, suggestions[this.state.highlightedSuggestion].value);
            return;
        }

        if (event.key === 'Enter' || event.key === ',') {
            event.preventDefault();
            this._commitEditingChip(index);
            return;
        }

        if (event.key === 'Backspace' && text === '') {
            const currentValues = this.currentFilters()[index]?.values || [];
            if (currentValues.length > 0) {
                event.preventDefault();
                this._removeChipAt(index, currentValues.length - 1);
            }
        }
    }

    _handleChipInputChange(index, event) {
        const text = event.target.value;
        // Si el usuario pega "foo, bar, baz", commitear todos
        if (text.includes(',')) {
            const parts = text.split(',').map(p => p.trim()).filter(Boolean);
            if (parts.length > 0) {
                const newFilters = [...this.currentFilters()];
                const existing = newFilters[index] || {};
                const currentValues = Array.isArray(existing.values) ? existing.values : [];
                const added = parts.filter(p => !currentValues.includes(p));
                newFilters[index] = {...existing, values: [...currentValues, ...added]};
                this._clearEditingText(index);
                this.emitChange(newFilters);
                return;
            }
        }
        this._setEditingText(index, text);
        this.setState({suggestionsOpenIndex: index, highlightedSuggestion: -1});
    }

    _handleChipInputBlur(index) {
        this._commitEditingChip(index);
        this._closeSuggestions();
    }

    // Al tomar foco se piden los valores reales del campo (una vez por property y campo: la capa
    // de API los cachea). Sin property elegida no hay nada que sugerir.
    _handleChipInputFocus(index, fieldId) {
        this.setState({suggestionsOpenIndex: index, highlightedSuggestion: -1});
        const {propertyId} = this.props;
        if (!propertyId || this.state.suggestionsByField[fieldId]) return;
        fetchGa4DimensionValues(propertyId, fieldId).then(values => {
            // `null` = no se pudieron traer: no se guarda, así el próximo foco vuelve a pedir. Un
            // `[]` guardado cortaba los focos siguientes y el autocompletar quedaba mudo (C-GA4PREV-08).
            if (!Array.isArray(values)) return;
            if (!this._mounted || this.props.propertyId !== propertyId) return;
            this.setState(prev => ({suggestionsByField: {...prev.suggestionsByField, [fieldId]: values}}));
        });
    }

    _closeSuggestions() {
        this.setState({suggestionsOpenIndex: null, highlightedSuggestion: -1});
    }

    _pickSuggestion(index, value) {
        this._addValue(index, value);
        this.setState({highlightedSuggestion: -1});
    }

    // Lo que coincide con lo que se está escribiendo, sin los valores ya elegidos, de más a menos usado.
    // En una cuenta de Meta, la Fuente y el Medio de Meta van primero: cada agencia los nombra a su
    // manera y entre los más usados de la propiedad quedaban abajo de google, direct y organic.
    _matchingSuggestions(index) {
        const filter = this.currentFilters()[index] || {};
        const fieldId = filter.field || this.availableFields()[0];
        const all = this.state.suggestionsByField[fieldId] || [];
        const chosen = new Set(Array.isArray(filter.values) ? filter.values : []);
        const text = (this.state.editingTextByIndex[index] || '').trim().toLowerCase();
        const matching = all
            .filter(s => !chosen.has(s.value) && (!text || String(s.value).toLowerCase().includes(text)));
        const metaAccount = this.props.channel === FACEBOOK_CHANNEL
            && (fieldId === FIELD_SOURCE || fieldId === FIELD_MEDIUM);
        return (metaAccount ? metaFirst(matching) : matching).slice(0, MAX_SUGGESTIONS);
    }

    componentDidUpdate(prevProps) {
        // Otra property = otros valores: los sugeridos de la anterior no sirven.
        if (prevProps.propertyId !== this.props.propertyId && Object.keys(this.state.suggestionsByField).length) {
            // eslint-disable-next-line react/no-did-update-set-state
            this.setState({suggestionsByField: {}});
        }
    }

    render() {
        const {t} = this.props;
        const filters = this.currentFilters();
        const fields = this.availableFields();
        const hasFilters = filters.length > 0;

        const rows = [];
        filters.forEach((f, index) => {
            if (index > 0) {
                rows.push(
                    <div key={`and-${index}`} className="ga4-filters-editor-and-separator">
                        <span>{t('ga4_filters.and_separator')}</span>
                    </div>
                );
            }
            rows.push(this._renderRow(f, index, fields, t));
        });

        return (
            <div className="ga4-filters-editor">
                <div className="ga4-filters-editor-header">
                    <FaIcon iconName="filter" className="ga4-filters-editor-header-icon" />
                    <span>{t('ga4_filters.section_title')}</span>
                </div>

                <div className="ga4-filters-editor-note">
                    {t('ga4_filters.granularity_note')}
                </div>

                {!hasFilters && (
                    <div className="ga4-filters-editor-empty">
                        {t('ga4_filters.no_filters')}
                    </div>
                )}

                {rows}

                {this.duplicatedFields().map(field => (
                    <div key={`duplicated-${field}`} className="ga4-filters-editor-note -duplicated-field" role="status">
                        <FaIcon iconName="exclamation-triangle" className="ga4-filters-editor-note-icon"/>
                        <span>
                            {t('ga4_filters.duplicated_field_warning', {
                                field: t(`ga4_filters.field_${field.toLowerCase()}`),
                            })}
                        </span>
                    </div>
                ))}

                {this.props.propertyId && (
                    <GA4FiltersPreview propertyId={this.props.propertyId} filters={filters}
                                       muted={this.duplicatedFields().length > 0}
                                       adAccountId={this.props.adAccountId} channel={this.props.channel}/>
                )}

                <div className="ga4-filters-editor-actions">
                    <button
                        type="button"
                        className="ga4-filters-editor-add"
                        onClick={this.handleAddFilter}
                    >
                        <span className="ga4-filters-editor-add-icon">+</span>
                        {t('ga4_filters.add_filter')}
                    </button>
                    {hasFilters && (
                        <span className="ga4-filters-editor-count">
                            {t('ga4_filters.filter_count', {count: filters.length})}
                        </span>
                    )}
                </div>
            </div>
        );
    }

    _renderRow(f, index, fields, t) {
        const fieldId = f.field || fields[0];
        const accentClass = FIELD_TO_ACCENT_CLASS[fieldId] || '';
        const operator = f.operator || OPERATOR_CONTAINS;

        return (
            <div
                key={index}
                className={`ga4-filters-editor-row ${accentClass}`}
            >
                <select
                    className="ga4-filters-editor-field"
                    value={fieldId}
                    onChange={(e) => this.handleFieldChange(index, e)}
                    aria-label={t('ga4_filters.field_label')}
                >
                    {this._renderFieldOptions(fields, t)}
                </select>

                <select
                    className="ga4-filters-editor-operator"
                    value={operator}
                    onChange={(e) => this.handleOperatorChange(index, e)}
                    aria-label={t('ga4_filters.operator_label')}
                >
                    {ALL_OPERATORS.map(op => (
                        <option key={op} value={op}>
                            {t(`ga4_filters.operator_${op.toLowerCase()}`)}
                        </option>
                    ))}
                </select>

                {this._renderValuesInput(f, index, fieldId, operator, t)}

                <button
                    type="button"
                    className="ga4-filters-editor-remove"
                    aria-label={t('ga4_filters.remove_filter')}
                    onClick={() => this.handleRemoveFilter(index)}
                >
                    ×
                </button>
            </div>
        );
    }

    _renderFieldOptions(fields, t) {
        const option = fId => (
            <option key={fId} value={fId}>
                {t(`ga4_filters.field_${fId.toLowerCase()}`)}
            </option>
        );
        const newFields = fields.filter(fId => NEW_FIELDS.includes(fId));
        return (
            <>
                {fields.filter(fId => !NEW_FIELDS.includes(fId)).map(option)}
                {newFields.length > 0 && (
                    <optgroup label={t('ga4_filters.new_fields_group')}>
                        {newFields.map(option)}
                    </optgroup>
                )}
            </>
        );
    }

    _renderValuesInput(filter, index, fieldId, operator, t) {
        const values = Array.isArray(filter.values) ? filter.values : [];
        const isMultiValue = MULTI_VALUE_OPERATORS.includes(operator);

        if (isMultiValue) {
            const editingText = this.state.editingTextByIndex[index] || '';
            const showHint = values.length === 0 && editingText === '';
            return (
                <div className="ga4-filters-editor-chips" onClick={(e) => this._focusChipInput(e)}>
                    {values.map((value, chipIndex) => (
                        <span key={`${value}-${chipIndex}`} className="ga4-filters-editor-chip">
                            {/* El chip corta con «…» (un id de conjunto de 18 dígitos no entra a 390):
                                el valor entero queda a la vista al pasar el mouse, como en las sugerencias. */}
                            <span className="ga4-filters-editor-chip-text" title={value}>{value}</span>
                            <button
                                type="button"
                                className="ga4-filters-editor-chip-remove"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    this._removeChipAt(index, chipIndex);
                                }}
                                aria-label={t('ga4_filters.remove_value', {value})}
                            >
                                ×
                            </button>
                        </span>
                    ))}
                    <input
                        type="text"
                        className="ga4-filters-editor-chip-input"
                        value={editingText}
                        placeholder={values.length === 0 ? this._placeholderFor(fieldId, operator, t) : ''}
                        onChange={(e) => this._handleChipInputChange(index, e)}
                        onKeyDown={(e) => this._handleChipInputKeyDown(index, e)}
                        onFocus={() => this._handleChipInputFocus(index, fieldId)}
                        onBlur={() => this._handleChipInputBlur(index)}
                        aria-label={t('ga4_filters.values_label')}
                        role="combobox"
                        aria-autocomplete="list"
                        aria-controls={this._suggestionsId(index)}
                        aria-expanded={this.state.suggestionsOpenIndex === index && this._matchingSuggestions(index).length > 0}
                    />
                    {showHint && (
                        <span className="ga4-filters-editor-chip-hint">
                            {t('ga4_filters.multi_value_hint')}
                        </span>
                    )}
                    {this._renderSuggestions(index, t)}
                </div>
            );
        }

        const isRegex = operator === OPERATOR_REGEXP_PARTIAL_MATCH;
        const singleClass = [
            'ga4-filters-editor-values',
            isRegex ? 'is-regex' : '',
        ].filter(Boolean).join(' ');

        return (
            <input
                type="text"
                className={singleClass}
                value={values[0] || ''}
                placeholder={this._placeholderFor(fieldId, operator, t)}
                onChange={(e) => this.handleSingleValueChange(index, e)}
                aria-label={t('ga4_filters.values_label')}
            />
        );
    }

    // Único por instancia del editor: una línea con dos cuentas monta dos editores a la vez.
    _suggestionsId(index) {
        if (!this._instanceId) {
            GA4FiltersEditorClass._instances = (GA4FiltersEditorClass._instances || 0) + 1;
            this._instanceId = GA4FiltersEditorClass._instances;
        }
        return `ga4-filters-suggestions-${this._instanceId}-${index}`;
    }

    _renderSuggestions(index, t) {
        if (this.state.suggestionsOpenIndex !== index) return null;
        const suggestions = this._matchingSuggestions(index);
        if (suggestions.length === 0) return null;
        return (
            <ul className="ga4-filters-editor-suggestions" role="listbox" id={this._suggestionsId(index)}
                aria-label={t('ga4_filters.suggestions_label')}>
                {suggestions.map((s, i) => (
                    <li key={s.value}
                        role="option"
                        aria-selected={i === this.state.highlightedSuggestion}
                        className={`ga4-filters-editor-suggestion${i === this.state.highlightedSuggestion ? ' -highlighted' : ''}`}
                        // mousedown y no click: el click llega DESPUÉS del blur del input, que ya
                        // habría commiteado el texto a medio escribir y cerrado la lista.
                        onMouseDown={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            this._pickSuggestion(index, s.value);
                        }}>
                        <span className="ga4-filters-editor-suggestion-value" title={s.value}>{s.value}</span>
                        <span className="ga4-filters-editor-suggestion-count">
                            {/* es-AR agrupa desde 4 cifras; `es` a secas no (QA-VIS-ga4prev-05). */}
                            {Math.round(s.count || 0).toLocaleString(String(this.props.i18n?.language || 'es').startsWith('en') ? 'en-US' : 'es-AR')}
                        </span>
                    </li>
                ))}
            </ul>
        );
    }

    _focusChipInput(event) {
        // Clic en cualquier parte del contenedor enfoca el input
        const input = event.currentTarget.querySelector('.ga4-filters-editor-chip-input');
        if (input) input.focus();
    }

    _placeholderFor(fieldId, operator, t) {
        const specific = `ga4_filters.values_placeholder_${operator.toLowerCase()}_${fieldId.toLowerCase()}`;
        const byOperator = `ga4_filters.values_placeholder_${operator.toLowerCase()}`;
        const fallback = t('ga4_filters.values_placeholder');

        const specificTranslation = t(specific, {defaultValue: ''});
        if (specificTranslation) {
            return specificTranslation;
        }
        const byOperatorTranslation = t(byOperator, {defaultValue: ''});
        if (byOperatorTranslation) {
            return byOperatorTranslation;
        }
        return fallback;
    }
}

export const GA4FiltersEditor = withTranslation()(GA4FiltersEditorClass);
export default GA4FiltersEditor;
